import React, { useEffect, useState } from "react";
import { Loader2 } from "lucide-react";
import { formatCompact } from "../../utils/formatCompact";
import { fetchAdminPredictionDetail } from "../api/fetchAdminPredictions";
import {
  formatPredictionAt,
  predictionTipLine,
} from "../../users/userPredictionHelpers";
import PredictionsFixtureBlock from "./PredictionsFixtureBlock";

function DetailStat({ label, value }) {
  return (
    <div className="amf-desk-stat">
      <span className="amf-desk-stat-val">{value}</span>
      <span className="amf-desk-stat-key">{label}</span>
    </div>
  );
}

function Panel({ title, children, empty }) {
  return (
    <section className="apr-panel">
      <h3 className="apr-panel-title">{title}</h3>
      {empty ? <p className="apr-panel-empty">{empty}</p> : children}
    </section>
  );
}

export default function PredictionsFloorDetail({ prediction }) {
  const [detail, setDetail] = useState(null);
  const [loadingDetail, setLoadingDetail] = useState(false);
  const [detailError, setDetailError] = useState(null);

  useEffect(() => {
    if (!prediction?._id) {
      setDetail(null);
      setDetailError(null);
      return undefined;
    }

    let cancelled = false;
    setLoadingDetail(true);
    setDetailError(null);

    fetchAdminPredictionDetail(prediction._id)
      .then((data) => {
        if (!cancelled) setDetail(data);
      })
      .catch((err) => {
        if (!cancelled) {
          setDetail(null);
          setDetailError(
            err.response?.data?.error || err.message || "Failed to load prediction detail"
          );
        }
      })
      .finally(() => {
        if (!cancelled) setLoadingDetail(false);
      });

    return () => {
      cancelled = true;
    };
  }, [prediction?._id]);

  if (!prediction) {
    return (
      <section className="amf-detail amf-detail-idle apr-detail" aria-label="Select a prediction">
        <div className="amf-idle">
          <p className="amf-idle-eyebrow">Predictions desk</p>
          <p className="amf-idle-title">Select a prediction from the ledger</p>
          <p className="amf-idle-copy">Fixture tips, outcomes, and engagement.</p>
        </div>
      </section>
    );
  }

  const row = detail?.prediction || prediction;
  const authorName = detail?.user?.name || row.userName || "Member";
  const authorEmail = detail?.user?.email || row.userEmail;

  return (
    <section className="amf-detail apr-detail" aria-label="Prediction detail">
      <header className="amf-desk amf-desk-with-question apr-desk-header">
        <div className="apr-desk-fixture">
          <p className="amf-desk-eyebrow">Fixture prediction</p>
          <PredictionsFixtureBlock
            prediction={row}
            as="h2"
            scalableLogos
            showTimingBadge={false}
          />
          <p className="amf-desk-meta apr-desk-meta">
            {authorName}
            {authorEmail ? ` · ${authorEmail}` : ""}
            {detail?.user?.inviteCode ? ` · ${detail.user.inviteCode}` : ""}
          </p>
        </div>

        <div className="amf-desk-stats apr-desk-stats">
          <DetailStat label="Views" value={String(Number(row.views) || 0)} />
          <DetailStat label="Price" value={formatCompact(Number(row.price) || 0)} />
          <DetailStat label="Outcomes" value={String(Number(row.outcomeCount) || 0)} />
        </div>
      </header>

      <div className="amf-detail-scroll apr-detail-scroll">
        {loadingDetail ? (
          <div className="apr-detail-loading">
            <Loader2 size={18} className="am-spin" aria-hidden />
            <span>Loading detail…</span>
          </div>
        ) : null}

        {detailError ? (
          <div className="apr-error-banner" role="alert">
            <span>{detailError}</span>
          </div>
        ) : null}

        <div className="apr-detail-timing">
          <div className="apr-detail-time">
            <span className="apr-detail-time-key">Posted</span>
            <span className="apr-detail-time-val">{formatPredictionAt(row.createdAt)}</span>
          </div>
          <div className="apr-detail-time">
            <span className="apr-detail-time-key">Tip</span>
            <span className="apr-detail-time-val">{predictionTipLine(row)}</span>
          </div>
          {detail?.leagueName ? (
            <div className="apr-detail-time">
              <span className="apr-detail-time-key">League</span>
              <span className="apr-detail-time-val">{detail.leagueName}</span>
            </div>
          ) : null}
          {detail?.fixtureDate ? (
            <div className="apr-detail-time">
              <span className="apr-detail-time-key">Kickoff</span>
              <span className="apr-detail-time-val">{formatPredictionAt(detail.fixtureDate)}</span>
            </div>
          ) : null}
        </div>

        {!loadingDetail && !detailError ? (
          <>
            <Panel title="Engagement">
              <div className="apr-kv-grid">
                <div>
                  <span>Likes</span>
                  <strong>{detail?.likes ?? row.likesCount ?? 0}</strong>
                </div>
                <div>
                  <span>Unlocks</span>
                  <strong>{detail?.unlocks ?? row.unlocksCount ?? 0}</strong>
                </div>
                <div>
                  <span>Outcomes</span>
                  <strong>{String(Number(row.outcomeCount) || 0)}</strong>
                </div>
                <div>
                  <span>Avg confidence</span>
                  <strong>{row.avgConfidence != null ? `${row.avgConfidence}%` : "—"}</strong>
                </div>
              </div>
            </Panel>

            <Panel
              title="Outcomes"
              empty={
                !(detail?.outcomes?.length || row.outcomeCount) ? "No outcomes listed." : null
              }
            >
              {detail?.outcomes?.length ? (
                <div className="apr-outcome-list">
                  {detail.outcomes.map((outcomeRow, idx) => (
                    <div key={`${outcomeRow.outcome}-${idx}`} className="apr-outcome-row">
                      <strong>{outcomeRow.outcome}</strong>
                      <span>{outcomeRow.confidence}%</span>
                      {outcomeRow.analysis ? <p>{outcomeRow.analysis}</p> : null}
                    </div>
                  ))}
                </div>
              ) : null}
            </Panel>

            {detail?.bettingAdvice ? (
              <Panel title="Advice">
                <p className="apr-advice">{detail.bettingAdvice}</p>
              </Panel>
            ) : null}
          </>
        ) : null}
      </div>
    </section>
  );
}
